import { queryOptions, mutationOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import {
  deleteSubmissionRow,
  fetchAiresStatuses,
  fetchLoansMappingData,
  fetchLoansTypes,
  fetchSharesMappingData,
  fetchSharesTypes,
  getAiresFilesData,
  getAiresMappingStatus,
  getDataPrepareDownloadStatus,
  getDataPrepareInfo,
  getDataQualityReport,
  getIsAreaEmpty,
  getSubmissionData,
  savePrefilledTemplate,
  uploadFilesData,
  fetchPfcMappingData,
  fetchPfcMeaningData,
  savePfcRow,
  savePfcRows,
  savePfcDefinition,
  getSubmissionHistory,
  getUiStatus,
  updateUiStatus,
} from '@/api';
import { QUERY_KEYS, UI_STATUS_TYPE } from '@/constants';
import {
  AiresFileData,
  AiresFilesIds,
  AssignMappingDataItem,
  MappingTypeItemBody,
  OSType,
  PfcRowRequestItem,
  ServerResponse,
  PfcDefinitionParams,
  PfcMappingRow,
  UiStatusUpdateRequest,
} from '@/types';

export const dataPreparerDownloadStatusQuery = (osType?: OSType) =>
  queryOptions({
    queryKey: [QUERY_KEYS.dataPreparerDownloadStatus, osType],
    queryFn: () => getDataPrepareDownloadStatus(osType),
    enabled: !!osType,
  });

export const dataPreparerInfoQuery = () =>
  queryOptions({
    queryKey: [QUERY_KEYS.dataPreparerInfo],
    queryFn: getDataPrepareInfo,
  });

export const airesFilesIsEmptyQuery = (refetchInterval: false | number = 3600) =>
  queryOptions({
    queryKey: [QUERY_KEYS.airesFilesIsEmpty],
    queryFn: getIsAreaEmpty,
    refetchInterval,
  });

export const getSubmissionsQuery = (enabled: boolean) =>
  queryOptions({
    queryKey: [QUERY_KEYS.submissions, enabled],
    queryFn: () => getSubmissionData(),
    enabled,
  });

export const airesFilesDataQuery = (params: string) =>
  queryOptions({
    queryKey: [QUERY_KEYS.airesData, params],
    queryFn: () => getAiresFilesData(params),
  });

export const airesFilesStatusesQuery = (submissionIds: AiresFilesIds, enabled: boolean) =>
  queryOptions({
    queryKey: [QUERY_KEYS.airesFilesStatuses, submissionIds],
    queryFn: () => fetchAiresStatuses(submissionIds),
    enabled: !!submissionIds.length || enabled,
    refetchInterval: !!submissionIds.length || enabled ? 5000 : false,
  });

export const dataQualityReportQuery = (submissionId: AiresFileData['submissionId']) =>
  queryOptions({
    queryKey: [QUERY_KEYS.airesFilesStatuses, submissionId],
    queryFn: () => getDataQualityReport(submissionId),
    enabled: !!submissionId,
  });

export const airesMappingStatusQuery = () =>
  queryOptions({
    queryKey: [QUERY_KEYS.airesMappingStatus],
    queryFn: getAiresMappingStatus,
  });

export const loansMappingDataQuery = (searchParams: string) =>
  queryOptions({
    queryKey: [QUERY_KEYS.loansMappingData, searchParams],
    queryFn: () => fetchLoansMappingData(searchParams),
  });

export const loansTypesQuery = () =>
  queryOptions({
    queryKey: [QUERY_KEYS.loansTypes],
    queryFn: fetchLoansTypes,
  });

export const sharesMappingDataQuery = (searchParams: string) =>
  queryOptions({
    queryKey: [QUERY_KEYS.sharesMappingData, searchParams],
    queryFn: () => fetchSharesMappingData(searchParams),
  });

export const sharesTypesQuery = () =>
  queryOptions({
    queryKey: [QUERY_KEYS.sharesTypes],
    queryFn: fetchSharesTypes,
  });

export const deleteSubmissionMutation = () =>
  mutationOptions({
    mutationFn: (submissionId: AiresFileData['submissionId']) => deleteSubmissionRow(submissionId),
  });

export const changeMappingDataMutation = (assignFn: (data: AssignMappingDataItem[]) => Promise<unknown>) =>
  mutationOptions({
    mutationKey: [QUERY_KEYS.assignMappingData],
    mutationFn: (selectedData: AssignMappingDataItem[]) => assignFn(selectedData),
  });

export const uploadFilesMutation = () =>
  mutationOptions({
    mutationKey: [QUERY_KEYS.airesMappingStatus],
    mutationFn: (formData: FormData) => uploadFilesData(formData),
  });

export const addNewTypeMutation = <TData = unknown>(submitFn: (type: MappingTypeItemBody) => Promise<TData>) =>
  mutationOptions({
    mutationFn: (type: MappingTypeItemBody) => submitFn(type),
  });

export const uploadPreparedFileMutation = () =>
  mutationOptions<ServerResponse<string>, AxiosError<ServerResponse<string>>, { url: string; data: FormData }>({
    mutationKey: [QUERY_KEYS.savePrefilledTemplate],
    mutationFn: ({ url, data }) => savePrefilledTemplate(url, data),
  });

export const pfcMappingDataQuery = (searchParams: string) =>
  queryOptions({
    queryKey: [QUERY_KEYS.pfcMappingData, searchParams],
    queryFn: () => fetchPfcMappingData(searchParams),
  });

export const pfcMeaningDataQuery = () =>
  queryOptions({
    queryKey: [QUERY_KEYS.pfcMeaningData],
    queryFn: fetchPfcMeaningData,
  });

export const mapPfcRow = () =>
  mutationOptions({
    mutationKey: [QUERY_KEYS.mapPfcRow],
    mutationFn: (editRows: PfcRowRequestItem) => savePfcRow(editRows),
  });

export const mapPfcRows = () =>
  mutationOptions({
    mutationKey: [QUERY_KEYS.mapPfcRows],
    mutationFn: (editRows: PfcMappingRow[]) => savePfcRows(editRows),
  });

export const submitPfcDefinition = () =>
  mutationOptions({
    mutationKey: [QUERY_KEYS.submitPfcDefinition],
    mutationFn: (params: PfcDefinitionParams) => savePfcDefinition(params),
  });

export const fetchSubmissionHistory = (params: string) =>
  queryOptions({
    queryKey: [QUERY_KEYS.submissionHistory, params],
    queryFn: () => getSubmissionHistory(params),
  });

export const uiStatusQuery = () =>
  queryOptions({
    queryKey: [QUERY_KEYS.uiStatus, UI_STATUS_TYPE.DataManagementTutorial],
    queryFn: () => getUiStatus(UI_STATUS_TYPE.DataManagementTutorial),
    staleTime: Infinity,
    gcTime: Infinity,
  });

export const updateUiStatusMutation = () =>
  mutationOptions({
    mutationKey: [QUERY_KEYS.updateUiStatus],
    mutationFn: (body: UiStatusUpdateRequest) => updateUiStatus(body),
  });
